import { Controller } from '@hotwired/stimulus';
import i18n, { pluralize } from '../lib/i18n';
import { hiddenField, paint } from '../lib/dom';
import { directories, inside, moved, parentOf } from '../lib/paths';

const DESKTOP = '(min-width: 768px)';

export default class extends Controller {
  static outlets = ['bundle-tree'];

  static targets = [
    'blank', 'changes', 'changeTemplate', 'deleteFolderModal', 'deleteModal',
    'deletedFile', 'deletedFolder', 'diffBody', 'diffFields', 'editor', 'editorBody', 'fileFields',
    'invalidSkillBadge', 'leaveModal', 'monaco', 'panel',
    'panelToggle', 'skillBadge', 'skillWarning', 'statusChanges', 'statusLanguage', 'statusPath', 'submit',
    'summary', 'tabTemplate', 'tabs',
  ];

  static values = {
    baseline: Array,
    bundleName: String,
    changeLabels: Object,
    fileField: String,
    files: Array,
    languages: Object,
    readOnly: Boolean,
    skillMessages: Object,
    skillWarning: String,
  };

  static classes = ['added', 'deleted', 'modified', 'tabActive', 'tabIdle'];

  initialize() {
    this.files = this.filesValue.map((file) => ({ ...file }));
    this.baseline = new Map(this.baselineValue.map((file) => [file.path, file.content]));
    this.emptyFolders = [];
    this.openTabs = this.files.length > 0 ? [this.files[0].path] : [];
    this.activePath = this.openTabs[0] ?? null;
    this.opened = null;
  }

  connect() {
    this.#render();

    if (!this.readOnlyValue) this.summaryChanged();

    this.warnBeforeUnload = (event) => {
      if (this.#changes().length > 0) event.preventDefault();
    };

    this.desktop = window.matchMedia(DESKTOP);
    this.revealPanel = () => {
      if (this.desktop.matches) this.#showPanel(true);
    };

    window.addEventListener('beforeunload', this.warnBeforeUnload);
    this.desktop.addEventListener('change', this.revealPanel);
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload);
    this.desktop.removeEventListener('change', this.revealPanel);
  }

  bundleTreeOutletConnected(tree) {
    tree.render(this.#treeScene());
  }

  togglePanel() {
    this.#showPanel(this.panelTarget.hidden);
  }

  guardVisit(event) {
    if (this.leaving || this.#changes().length === 0) return;

    event.preventDefault();

    this.destination = event.detail.url;
    this.leaveModalTarget.showModal();
  }

  confirmLeave() {
    this.leaving = true;

    this.leaveModalTarget.close();
    window.Turbo.visit(this.destination);
  }

  editorReady() {
    this.opened = null;

    this.#renderEditor();
  }

  fileOpened({ detail: { path } }) {
    this.#open(path);
  }

  editStarted() {
    this.#showPanel(true);
  }

  entryCreated({ detail: { path, type } }) {
    if (type === 'folder') return this.#createFolder(path);

    this.#createFile(path);
  }

  entryRenamed({ detail: { from, to } }) {
    const moves = this.files
      .filter((file) => inside(file.path, from))
      .map((file) => [file.path, moved(file.path, from, to)]);

    for (const [before, after] of moves) this.#movePath(before, after);

    this.files = this.files.map((file) => ({ ...file, path: moved(file.path, from, to) }));
    this.emptyFolders = this.emptyFolders.map((folder) => moved(folder, from, to));

    this.#keepFolder(parentOf(from));
    this.#render();
  }

  entryRemoved({ detail: { path, type } }) {
    if (type === 'folder') {
      this.doomedFolder = path;

      this.deletedFolderTarget.textContent = `${this.bundleNameValue}/${path}/`;

      return this.deleteFolderModalTarget.showModal();
    }

    this.doomed = path;

    this.deletedFileTarget.textContent = `${this.bundleNameValue}/${path}`;
    this.deleteModalTarget.showModal();
  }

  selectTab(event) {
    this.activePath = event.currentTarget.closest('[data-path]').dataset.path;

    this.#render();
  }

  closeTab(event) {
    const path = event.currentTarget.closest('[data-path]').dataset.path;

    this.#dropTab(path);
    this.#render();
  }

  confirmDeleteFile() {
    const path = this.doomed;

    this.deleteModalTarget.close();
    this.files = this.files.filter((file) => file.path !== path);

    if (this.opened === path) this.opened = null;

    this.#keepFolder(parentOf(path));
    this.#monaco?.forget(path);
    this.#dropTab(path);
    this.#render();
  }

  confirmDeleteFolder() {
    const folder = this.doomedFolder;
    const doomed = this.files.filter((file) => inside(file.path, folder));

    this.deleteFolderModalTarget.close();

    for (const file of doomed) {
      if (this.opened === file.path) this.opened = null;

      this.#monaco?.forget(file.path);
      this.#dropTab(file.path);
    }

    this.files = this.files.filter((file) => !inside(file.path, folder));
    this.emptyFolders = this.emptyFolders.filter((candidate) => !inside(candidate, folder));

    this.#keepFolder(parentOf(folder));
    this.#render();
  }

  summaryChanged() {
    const disabled = this.summaryTarget.value.trim().length === 0;

    this.submitTarget.disabled = disabled;
    this.submitTarget.parentElement.title = disabled
      ? i18n.t('components.bundles.editor_component.save_revision_disabled')
      : '';
  }

  contentChanged({ detail: { content } }) {
    const file = this.files.find((candidate) => candidate.path === this.activePath);

    file.content = content;

    this.#render();
  }

  serialize() {
    this.leaving = true;

    this.#fill(this.fileFieldsTarget, this.fileFieldValue);
  }

  serializeDiff() {
    this.#fill(this.diffFieldsTarget, 'files[]');
  }

  switchMode({ detail: { mode } }) {
    this.editorBodyTarget.hidden = mode !== 'files';
    this.diffBodyTarget.hidden = mode === 'files';
  }

  #fill(container, prefix) {
    container.replaceChildren();

    for (const file of this.files) {
      container.appendChild(hiddenField(`${prefix}[path]`, file.path));
      container.appendChild(hiddenField(`${prefix}[content]`, file.content));
    }
  }

  #createFolder(path) {
    if (!this.#directories().has(path)) this.emptyFolders = [...this.emptyFolders, path];

    this.#render();
  }

  #createFile(path) {
    if (!this.files.some((file) => file.path === path)) {
      this.files = [...this.files, { path, content: '' }];
      this.emptyFolders = this.emptyFolders.filter((folder) => !path.startsWith(`${folder}/`));
    }

    this.#open(path);
  }

  #open(path) {
    this.activePath = path;

    if (!this.openTabs.includes(path)) this.openTabs = [...this.openTabs, path];
    if (!this.desktop.matches) this.#showPanel(false);

    this.#render();
  }

  #showPanel(shown) {
    this.panelTarget.hidden = !shown;
    this.panelToggleTarget.setAttribute('aria-expanded', String(shown));
    this.panelToggleTarget.querySelector('[data-role="chevron"]').classList.toggle('-rotate-90', !shown);
  }

  #dropTab(path) {
    this.openTabs = this.openTabs.filter((candidate) => candidate !== path);

    if (this.activePath === path) this.activePath = this.openTabs.at(-1) ?? null;
  }

  #keepFolder(folder) {
    if (folder === '' || this.#directories().has(folder)) return;

    this.emptyFolders = [...this.emptyFolders, folder];
  }

  #movePath(from, to) {
    this.#monaco?.forget(from);
    this.openTabs = this.openTabs.map((path) => (path === from ? to : path));

    if (this.activePath === from) this.activePath = to;
    if (this.opened === from) this.opened = null;
  }

  #render() {
    this.#tree?.render(this.#treeScene());
    this.#renderTabs();
    this.#renderEditor();
    this.#renderStatus();
    this.#renderSkill();
    this.#renderChanges();

    this.dispatch('changed', { target: window, detail: { count: this.#changes().length } });
  }

  #renderTabs() {
    this.tabsTarget.hidden = this.openTabs.length === 0;
    this.tabsTarget.replaceChildren(...this.openTabs.map((path) => this.#tab(path)));
  }

  #renderEditor() {
    this.editorTarget.hidden = this.activePath === null;
    this.blankTarget.hidden = this.activePath !== null;

    if (!this.#monaco || this.activePath === null || this.activePath === this.opened) return;

    this.opened = this.activePath;

    const file = this.files.find((candidate) => candidate.path === this.activePath);

    this.#monaco.open(file.path, file.content, this.#language(file.path));
  }

  get #monaco() {
    return this.application.getControllerForElementAndIdentifier(this.monacoTarget, 'monaco');
  }

  get #tree() {
    return this.hasBundleTreeOutlet ? this.bundleTreeOutlet : null;
  }

  #treeScene() {
    return {
      files: this.files.map((file) => ({ path: file.path, dirty: this.#dirty(file) })),
      emptyFolders: this.emptyFolders,
      activePath: this.activePath,
    };
  }

  #renderStatus() {
    const count = this.#changes().length;

    this.statusPathTarget.textContent = this.activePath ? `${this.bundleNameValue}/${this.activePath}` : '';
    this.statusLanguageTarget.textContent = this.activePath ? this.#language(this.activePath) : '';
    this.statusChangesTarget.hidden = count === 0;
    this.statusChangesTarget.textContent = pluralize(this.changeLabelsValue, count);
  }

  #renderSkill() {
    const errors = this.#skillErrors();

    this.skillBadgeTarget.hidden = errors === null || errors.length > 0;
    this.invalidSkillBadgeTarget.hidden = errors === null || errors.length === 0;

    if (this.readOnlyValue) return;

    this.skillWarningTarget.hidden = this.invalidSkillBadgeTarget.hidden;

    if (this.skillWarningTarget.hidden) return;

    this.skillWarningTarget.textContent = this.skillWarningValue.replace('%{errors}', errors.join(' · '));
  }

  #renderChanges() {
    if (this.readOnlyValue) return;

    const tones = { A: this.addedClasses, M: this.modifiedClasses, D: this.deletedClasses };

    const rows = this.#changes().map(({ kind, path }) => {
      const row = this.changeTemplateTarget.content.firstElementChild.cloneNode(true);

      row.querySelector('[data-role="kind"]').textContent = kind;
      row.querySelector('[data-role="kind"]').classList.add(...tones[kind]);
      row.querySelector('[data-role="path"]').textContent = `${this.bundleNameValue}/${path}`;

      return row;
    });

    this.changesTarget.replaceChildren(...rows);
  }

  #changes() {
    const paths = new Set(this.files.map((file) => file.path));

    return [
      ...this.files.filter((file) => !this.baseline.has(file.path)).map((file) => ({ kind: 'A', path: file.path })),
      ...this.files
        .filter((file) => this.baseline.has(file.path) && this.baseline.get(file.path) !== file.content)
        .map((file) => ({ kind: 'M', path: file.path })),
      ...[...this.baseline.keys()].filter((path) => !paths.has(path)).map((path) => ({ kind: 'D', path })),
    ];
  }

  #skillErrors() {
    const skill = this.files.find((file) => file.path === 'SKILL.md');

    if (!skill) return null;

    const frontmatter = skill.content.match(/^---\n([\s\S]*?)\n---/);

    if (!frontmatter) return [this.skillMessagesValue.missing_frontmatter];

    return [...this.#nameErrors(frontmatter[1]), ...this.#descriptionErrors(frontmatter[1])];
  }

  #nameErrors(frontmatter) {
    const declared = frontmatter.match(/^name:\s*(.+)$/m)?.[1]?.trim();

    if (!declared) return [this.skillMessagesValue.missing_name];
    if (declared === this.bundleNameValue) return [];

    return [
      this.skillMessagesValue.name_mismatch
        .replace('%{name}', declared)
        .replace('%{bundle_name}', this.bundleNameValue),
    ];
  }

  #descriptionErrors(frontmatter) {
    const declared = frontmatter.match(/^description:\s*(.+)$/m)?.[1]?.trim();

    return declared ? [] : [this.skillMessagesValue.missing_description];
  }

  #directories() {
    return directories(
      this.files.map((file) => file.path),
      this.emptyFolders
    );
  }

  #tab(path) {
    const tab = this.tabTemplateTarget.content.firstElementChild.cloneNode(true);
    const file = this.files.find((candidate) => candidate.path === path);
    const active = path === this.activePath;

    tab.dataset.path = path;
    tab.querySelector('[data-role="name"]').textContent = path.split('/').pop();
    tab.querySelector('[data-role="dirty"]').hidden = !this.#dirty(file);
    tab.querySelector('[role="tab"]').setAttribute('aria-selected', String(active));
    paint(tab, active, this.tabActiveClasses, this.tabIdleClasses);

    return tab;
  }

  #dirty(file) {
    return !this.baseline.has(file.path) || this.baseline.get(file.path) !== file.content;
  }

  #language(path) {
    return this.languagesValue[path.slice(path.lastIndexOf('.'))] ?? 'plaintext';
  }


}
